import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { useAdminData, useRegistrationActions } from "../../hooks/useAdminData";
import { ConfigEditor } from "./ConfigEditor";
import { ChangePassword } from "./ChangePassword";
import { Sidebar, MobileNav } from "./AdminNav";
import { OverviewTab, RegistrationsTab, GiftsTab, ExportsTab, AuditTab } from "./AdminTabs";
import { SponsorshipsTab } from "./SponsorshipsTab";
import { RegistrationModal } from "./RegistrationModal";

const TAB_LOADERS = { registrations: "loadRegs", config: "loadConfig", audit: "loadAudit", sponsorships: "loadSpons" };
const exportCsv = (kind) => { window.open(`${API}/admin/export/${kind}`, "_blank"); };

function TabContent({ tab, d, onView, regActions }) {
  if (tab === "overview") return d.stats && <OverviewTab stats={d.stats} />;
  if (tab === "registrations") return <RegistrationsTab regs={d.regs} filters={d.filters} onView={onView} onDelete={regActions.remove} />;
  if (tab === "gifts") return d.gifts && <GiftsTab gifts={d.gifts} />;
  if (tab === "sponsorships") return d.spons && <SponsorshipsTab spons={d.spons} act={d.sponsorAction} reload={d.loadSpons} onExport={exportCsv} />;
  if (tab === "config") return d.config && <ConfigEditor config={d.config} onSaved={(c) => { d.setConfig(c); toast.success("Configuration saved"); }} />;
  if (tab === "exports") return <ExportsTab onExport={exportCsv} />;
  if (tab === "audit") return <AuditTab audit={d.audit} />;
  if (tab === "account") return <ChangePassword />;
  return null;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [authed, setAuthed] = useState(null);
  const [selected, setSelected] = useState(null);
  const d = useAdminData();
  const { loadStats, loadGifts, loadRegs } = d;

  const refreshAfterChange = useCallback(() => { loadStats(); loadGifts(); loadRegs(); setSelected(null); }, [loadStats, loadGifts, loadRegs]);
  const regActions = useRegistrationActions(refreshAfterChange);

  useEffect(() => {
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => { setAuthed(false); navigate("/admin"); });
    const id = api.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 401) { toast.error("Your session has ended. Please log in again."); navigate("/admin"); }
      return Promise.reject(err);
    });
    return () => { api.interceptors.response.eject(id); };
  }, [navigate]);
  useEffect(() => { if (authed) { loadStats(); loadGifts(); } }, [authed, loadStats, loadGifts]);
  const tabLoader = d[TAB_LOADERS[tab]];
  useEffect(() => { if (authed && tabLoader) tabLoader(); }, [authed, tabLoader]);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch { /* session already ended */ }
    finally { navigate("/admin"); }
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAF8] md:flex">
      <Sidebar tab={tab} setTab={setTab} onLogout={logout} />
      <MobileNav tab={tab} setTab={setTab} onLogout={logout} />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden" data-testid="admin-dashboard">
        <TabContent tab={tab} d={d} onView={setSelected} regActions={regActions} />
      </main>
      {selected && (
        <RegistrationModal order={selected} actions={regActions} onClose={() => setSelected(null)}
          onUpdated={(o) => { setSelected(o); loadRegs(); }} onStatusChanged={refreshAfterChange} />
      )}
    </div>
  );
}
